import { useEffect, useState } from "react";
import { Modal } from "../../shared";
import { ImageUploadField } from "../../atoms";
import LabelPicker from "./LabelPicker";
import MetadataFieldsEditor from "./MetadataFieldsEditor";

/**
 * Parça tipi oluştur/düzenle (D-058).
 * part=null → yeni kayıt. codeLocked → o tipten ünite üretilmiş, kod değişmez.
 */
export default function PartTypeModal({
  open,
  part,
  allParts = [],
  machineTypes = [],
  families = [],
  onClose,
  onSubmit,
  saving,
}) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: part?.name || "",
      code: part?.code || "",
      families: (part?.families || []).map((f) => String(f._id || f)),
      subParts: (part?.subParts || []).map((s) => s._id || s),
      machineTypeIds: (part?.machineTypeIds || []).map(String),
      hasQrCode: Boolean(part?.hasQrCode),
      hasBarcode: Boolean(part?.hasBarcode),
      metadataFields: (part?.metadataFields || []).map((f) => ({
        key: f.key || "",
        unique: Boolean(f.unique),
      })),
      isActive: part ? part.isActive : true,
      // Görsel ayrı endpoint'ten gider (multipart): burada yalnız niyet tutulur.
      // imageFile = yeni seçilen dosya, imageCleared = mevcut görseli kaldır.
      image: part?.image || null,
      imageFile: null,
      imageCleared: false,
    });
  }, [open, part]);

  if (!open || !form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  /** null = mevcut görseli kaldır, File = yeni görsel yükle. */
  const setImage = (picked) =>
    setForm((f) => ({
      ...f,
      imageFile: picked,
      imageCleared: picked === null,
      image: picked === null ? null : f.image,
    }));
  const toggleIn = (k, id) =>
    set(k, form[k].includes(id) ? form[k].filter((x) => x !== id) : [...form[k], id]);

  const codeLocked = Boolean(part?.codeLocked);
  // Seri kodu benzersiz olmalı — sunucu da 409 döner, burada anında uyarıyoruz
  const codeTaken = allParts.some(
    (p) =>
      String(p._id) !== String(part?._id || "") &&
      String(p.code || "").toUpperCase() === form.code.trim().toUpperCase()
  );
  // Kendisi ve kendisini içerenler alt parça olamaz (basit UI koruması; asıl döngü kontrolü server'da)
  const subPartCandidates = allParts.filter((p) => String(p._id) !== String(part?._id || ""));
  // Ad + benzersiz kod + en az bir ürün ailesi zorunlu (D-064)
  const valid =
    form.name.trim() && form.code.trim().length >= 2 && !codeTaken && form.families.length > 0;

  return (
    <Modal open={open} onClose={onClose} maxWidth="lg">
      <Modal.Header
        title={part ? "Parça Tipini Düzenle" : "Yeni Parça Tipi"}
        subtitle={part ? part.name : "Katalog kaydı — fiziksel üretim tabletten yapılır"}
        onClose={onClose}
      />
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Parça Adı</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Örn. DEĞİRMEN"
                className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">
                Seri Kodu {codeLocked && <span className="text-amber-600">(kilitli)</span>}
              </label>
              <input
                type="text"
                value={form.code}
                disabled={codeLocked}
                onChange={(e) => set("code", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5))}
                placeholder="DGR"
                className={`w-full border rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 disabled:bg-gray-50 disabled:text-gray-400 ${
                  codeTaken
                    ? "border-red-400 focus:ring-red-200"
                    : "border-input focus:ring-indigo-200"
                }`}
              />
              {codeTaken ? (
                <p className="mt-1 text-[10px] font-semibold text-red-500">
                  Bu kod başka bir parçada kullanılıyor — seri kodu benzersiz olmalı.
                </p>
              ) : (
                <p className="mt-1 text-[10px] text-gray-400">
                  Üretilen parçanın serisi: <b>{form.code || "???"}</b>-0001.
                  {codeLocked && " Üretilmiş parça olduğu için değiştirilemez."}
                </p>
              )}
            </div>
          </div>

          <ImageUploadField
            label="Parça Görseli"
            hint="(tablette üretim ekranında operatöre gösterilir)"
            value={form.image}
            file={form.imageFile}
            onChange={setImage}
            disabled={saving}
          />

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              Ürün Aileleri <span className="text-red-500">*</span>{" "}
              <span className="font-normal text-gray-400">
                (tablette üretim menüsünü gruplar; ortak parçalar birden fazla
                ailede olabilir)
              </span>
            </label>
            <LabelPicker
              labels={families}
              value={form.families}
              onChange={(v) => set("families", v)}
              multi
              emptyHint="Ürün ailesi tanımlı değil — Katalog > Ürün Aileleri sekmesinden ekleyin"
            />
            {form.families.length === 0 && (
              <p className="mt-1 text-[10px] font-semibold text-red-500">
                En az bir ürün ailesi seçilmeli.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              Kullanıldığı Makineler{" "}
              <span className="font-normal text-gray-400">
                (o makinenin BOM listesine eklenir — makine formundan da düzenlenebilir)
              </span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {machineTypes
                .filter((mt) => mt.origin === "produced")
                .map((mt) => (
                  <button
                    key={mt._id}
                    type="button"
                    onClick={() => toggleIn("machineTypeIds", String(mt._id))}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      form.machineTypeIds.includes(String(mt._id))
                        ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                        : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {mt.name}
                    <span className="ml-1 font-mono text-[10px] opacity-60">
                      {mt.code}
                    </span>
                  </button>
                ))}
              {machineTypes.filter((mt) => mt.origin === "produced").length === 0 && (
                <span className="text-xs text-gray-400">
                  Henüz üretim tipi makine yok — Makine Tipleri sekmesinden ekleyin
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              Alt Parçalar <span className="font-normal text-gray-400">(fiziksel olarak içinde — Değirmen ⊃ Bobin, Kart)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {subPartCandidates.map((p) => (
                <button
                  key={p._id}
                  type="button"
                  onClick={() => toggleIn("subParts", p._id)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    form.subParts.includes(p._id)
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {p.name}
                </button>
              ))}
              {subPartCandidates.length === 0 && (
                <span className="text-xs text-gray-400">Önce diğer parça tiplerini ekleyin</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={form.hasQrCode}
                onChange={(e) => set("hasQrCode", e.target.checked)}
                className="w-4 h-4"
              />
              QR kod var
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={form.hasBarcode}
                onChange={(e) => set("hasBarcode", e.target.checked)}
                className="w-4 h-4"
              />
              Barkod var
            </label>
          </div>

          <MetadataFieldsEditor
            value={form.metadataFields}
            onChange={(v) => set("metadataFields", v)}
          />

          {part && (
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => set("isActive", e.target.checked)}
                className="w-4 h-4"
              />
              Aktif (pasif tip tablette üretim listesinde görünmez)
            </label>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          onClick={onClose}
          className="px-3.5 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Vazgeç
        </button>
        <button
          onClick={() => onSubmit(form)}
          disabled={!valid || saving}
          className="px-3.5 py-2 text-sm font-semibold text-white bg-main rounded-lg hover:bg-mainSecond disabled:opacity-50"
        >
          {saving ? "Kaydediliyor..." : part ? "Kaydet" : "Oluştur"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
